import { fetchApiGetAiChatRatedMessages } from '@repo/api-codegen';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import type { TimeRangeWithAll } from '../types';

export const useInfiniteAiChatRatedMessages = ({
	timeRange,
	sortOrder,
	sentimentFilter,
	limit = 10,
}: {
	timeRange: TimeRangeWithAll;
	sortOrder: 'newest' | 'oldest';
	sentimentFilter?: 'positive' | 'negative';
	limit?: number;
}) => {
	const query = useInfiniteQuery({
		queryKey: [
			'ai-chat-rated-messages-infinite',
			timeRange,
			sortOrder,
			sentimentFilter,
			limit,
		],
		queryFn: async ({ pageParam = 0, signal }) => {
			const queryParams: {
				time_range: TimeRangeWithAll;
				sort_order: 'newest' | 'oldest';
				limit: number;
				offset: number;
				sentiment_filter?: 'positive' | 'negative';
			} = {
				time_range: timeRange,
				sort_order: sortOrder,
				limit,
				offset: pageParam,
			};

			if (sentimentFilter) {
				queryParams.sentiment_filter = sentimentFilter;
			}

			const response = await fetchApiGetAiChatRatedMessages(
				{
					queryParams,
				},
				signal
			);

			return response;
		},
		initialPageParam: 0,
		getNextPageParam: (lastPage, allPages) => {
			if (!lastPage.has_next_page) {
				return undefined;
			}
			const nextOffset = allPages.reduce(
				(acc, page) => acc + (page.items?.length ?? 0),
				0
			);
			return nextOffset;
		},
		refetchOnWindowFocus: false,
	});

	const allItems = useMemo(() => {
		const items = query.data?.pages.flatMap((page) => page.items) ?? [];
		return items;
	}, [query.data?.pages]);

	const totalCount = query.data?.pages[0]?.total_count ?? 0;

	return {
		...query,
		allItems,
		totalCount,
	};
};
